import { ExternalLink, Trash } from 'react-feather';
import { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { gql } from '__generated__';

import {
  GetAppsForProjectQuery as GetAppsForProject,
} from '__generated__/graphql';

import { useNotifier } from '../../common/Notifications/NotificationProvider';
import GetAppsForProjectQuery from './GetAppsForProjectQuery';
import OptionsDropdown, { Option } from '../../common/Common/OptionsDropdown';
import UI from '../../common/UI';
import useAddAppModal from './useAddAppModal';
import useConfirmModal from '../../common/useConfirmModal';
import useLocale from '../../common/useLocale';
import useMutation from '../../api/useMutation';
import useProject from '../useProject';
import useQuery from '../../api/useQuery';

type Project = GetAppsForProject['project'];
type App = Project['app_authorizations'][0]['app'];

export const RemoveAppMutation = gql(`
  mutation RemoveAppFromProject($project_id: ID!, $app_id: ID!) {
    removeAppFromProject(
      project: { id: $project_id },
      app: { id: $app_id }
    ) {
      id
      app_authorizations {
        app {
          id
          name
        }
      }
    }
  }
`);

interface AppOptionsDropdownProps {
  app: App;
}

const AppOptionsDropdown = ({ app }: AppOptionsDropdownProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const project = useProject();

  const [removeApp] = useMutation(
    RemoveAppMutation,
    {
      variables: {
        project_id: project.id,
        app_id: app.id,
      },
      onCompleted: () => {
        notifier.success(t('notifications.app_removed'));
      },
    },
  );

  const [confirmRemove] = useConfirmModal({
    title: t('remove_app'),
    body: (
      <Trans
        i18nKey="confirm_remove_app"
        values={{
          name: app.name,
          project: project.organisation_name,
        }}
      >
        <UI.Strong />
      </Trans>
    ),
    onConfirm: () => removeApp(),
  }, []);

  return (
    <OptionsDropdown name={app.name}>
      <Option
        icon={<Trash />}
        label={t('remove')}
        aria-label={t('remove_app')}
        onClick={() => confirmRemove()}
      />
    </OptionsDropdown>
  );
};

const AppsList = ({ project }: GetAppsForProject) => {
  const { formatDate, parseDate } = useLocale();

  return (
    <UI.GridContainer
      sc={{
        columns: '30px 2fr 1fr 34px',
        gutter: 0.5,
        alignVertical: 'center',
      }}
    >
      {project.app_authorizations.map(({ app }) => (
        <Fragment key={app.id}>
          <UI.Div>
            <UI.ProfilePic
              sc={{
                background: 'secondary',
                fit: 'cover',
                size: 30,
              }}
            >
              <UI.Span><UI.Span>{app.name.charAt(0).toUpperCase()}</UI.Span></UI.Span>
            </UI.ProfilePic>
          </UI.Div>
          <UI.Div>
            <UI.Truncate>
              {app.name}
            </UI.Truncate>
          </UI.Div>
          <UI.Div sc={{ color: 'gray.500' }}>
            {formatDate(parseDate(app.created_at), { format: 'display_compact_date' })}
          </UI.Div>
          <UI.Div>
            <AppOptionsDropdown app={app} />
          </UI.Div>
        </Fragment>
      ))}
    </UI.GridContainer>
  );
};

interface ApiPageProps {
  projectId: string;
}

const ApiPage = ({ projectId }: ApiPageProps) => {
  const { data } = useQuery(GetAppsForProjectQuery, {
    variables: {
      project_id: projectId,
    },
  });

  const project = data?.project;

  if (!project) {
    return <></>;
  }

  return <AppsView project={project} />;
};

interface AppsViewProps {
  project: Project;
}

const AppsView = ({ project }: AppsViewProps) => {
  const { t } = useTranslation();
  const hasAuthorizedApps = project.app_authorizations.length > 0;

  const [openAddAppModal] = useAddAppModal();

  return (
    <UI.Container sc={{ compact: true, px: [0, 4] }}>
      <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <UI.H3>
          {t('atleta_api')}
        </UI.H3>

        <UI.FlexContainer sc={{ spaceX: 1 }}>
          <UI.AButton
            href="https://atleta.cc/article/api"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('documentation')}
            {' '}
            <UI.Icon>
              <ExternalLink />
            </UI.Icon>
          </UI.AButton>
          <UI.Button
            onClick={openAddAppModal}
            sc={{ brand: 'secondary' }}
            style={{ whiteSpace: 'nowrap' }}
          >
            {t('add_app')}
          </UI.Button>
        </UI.FlexContainer>
      </UI.FlexContainer>

      <UI.InputDescription sc={{ my: 3 }}>
        {t('app_access_description')}
      </UI.InputDescription>

      <UI.Card
        sc={{
          hasPanels: true,
          textAlign: hasAuthorizedApps ? undefined : 'center',
          background: hasAuthorizedApps ? undefined : 'gray.50',
        }}
      >
        <UI.Div>
          {hasAuthorizedApps ? <AppsList project={project} /> : t('no_apps_yet')}
        </UI.Div>
      </UI.Card>
    </UI.Container>
  );
};

export default ApiPage;
